import type { DeviceKind } from "./audio-devices.store.js";
import { applyPreferredDevices } from "./rtk-device-apply.js";
import type { MeetingClient } from "./table-call.context.js";

interface DevicePreferences {
  preferredMicrophoneId: string | null;
  preferredSpeakerId: string | null;
}

interface SelectDeviceArgs {
  meeting: MeetingClient;
  deviceId: string;
}

export const applyPreferredDevicesForMeeting = (
  meeting: MeetingClient,
  preferences: DevicePreferences
) =>
  applyPreferredDevices({
    meeting,
    preferredMicrophoneId: preferences.preferredMicrophoneId,
    preferredSpeakerId: preferences.preferredSpeakerId,
  });

export const syncCurrentDevices = (meeting: MeetingClient) => {
  const currentDevices = meeting.self.getCurrentDevices();
  return {
    audioinput: currentDevices.audio?.deviceId ?? null,
    audiooutput: currentDevices.speaker?.deviceId ?? null,
  } satisfies Record<DeviceKind, string | null>;
};

export const selectInputDevice = async ({
  meeting,
  deviceId,
}: SelectDeviceArgs) => {
  // For some reason setDevice requires MediaDeviceInfo, so we cannot just pass the deviceId.
  const device = (await meeting.self.getAudioDevices()).filter(
    (d) => d.kind === "audioinput" && d.deviceId === deviceId
  )?.[0];

  if (!device) {
    throw new Error("The selected microphone is unavailable.");
  }

  await meeting.self.setDevice(device);
  return device;
};

export const selectOutputDevice = async ({
  meeting,
  deviceId,
}: SelectDeviceArgs) => {
  // For some reason setDevice requires MediaDeviceInfo, so we cannot just pass the deviceId.
  const device = (await meeting.self.getSpeakerDevices()).filter(
    (d) => d.kind === "audiooutput" && d.deviceId === deviceId
  )?.[0];

  if (!device) {
    throw new Error("The selected speaker is unavailable.");
  }

  await meeting.self.setDevice(device);
  return device;
};
